import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Pencil } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Field, Panel, StatusBadge } from "@/components/app/common";
import { AuditMeta, AuditPanel } from "@/components/app/AuditPanel";
import { DocumentsPanel } from "@/components/app/DocumentsPanel";
import { useMe } from "@/components/app/MeContext";
import { AssetUnitForm } from "@/components/assets/AssetUnitForm";
import { useFetch } from "@/hooks/useFetch";
import { api, errMsg } from "@/lib/api";
import { fmtDate } from "@/lib/format";

const DATES = [["installation_date", "Installation"], ["commissioning_date", "Commissioning"], ["handover_date", "Handover"]];
const LC = { preloaded: "Preloaded", pending_dispatch_confirmation: "Pending dispatch confirmation", active: "Active", installed: "Installed", inactive: "Inactive", decommissioned: "Decommissioned" };
const NEXT = { preloaded: [["active", "Activate (already on site, no challan)"], ["inactive", "Mark inactive"]], pending_dispatch_confirmation: [["inactive", "Mark inactive"]],
  active: [["inactive", "Mark inactive"], ["decommissioned", "Decommission"]], installed: [["inactive", "Mark inactive"], ["decommissioned", "Decommission"]],
  inactive: [["active", "Make active again"], ["decommissioned", "Decommission"]] };

function LifecycleAction({ u, onDone }) {
  const opts = NEXT[u.lifecycle_status] || [];
  if (!opts.length) return null;
  const go = async (to) => {
    const reason = window.prompt(`Reason (${opts.find((o) => o[0] === to)[1]})?`);
    if (!reason) return;
    try { await api.post(`/assets/units/${u.id}/lifecycle`, { status: to, reason }); toast.success("Status updated"); onDone(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <select className="h-9 rounded border border-slate-200 bg-white px-2 text-xs" value="" onChange={(e) => e.target.value && go(e.target.value)} data-testid="au-lifecycle-action">
      <option value="">Change life stage…</option>{opts.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
    </select>
  );
}

function LifecycleHistory({ u }) {
  return (
    <Panel title="Life stage & source" testid="au-lifecycle-panel">
      <div className="text-xs text-slate-600 mb-2" data-testid="au-source-summary"><b>{u.lifecycle_label}</b> · {u.addition_label}{u.source_order_no ? ` · LMO ${u.source_order_no}` : ""}{u.preloaded_at ? ` · preloaded ${fmtDate(u.preloaded_at)} by ${u.preloaded_by?.name}` : ""}
        {u.activated_at ? ` · activated ${fmtDate(u.activated_at)} via ${u.activated_dispatch_no} (DC ${u.activated_dc_no || "-"})` : ""}</div>
      <ul className="space-y-1 text-xs" data-testid="au-lifecycle-history">{(u.lifecycle_history || []).map((h) => (
        <li key={`${h.at}-${h.from}-${h.to}`} className="flex flex-wrap gap-2"><span className="text-slate-500">{fmtDate(h.at)}</span><b>{h.from ? `${LC[h.from] || h.from} → ` : ""}{LC[h.to] || h.to}</b><span>{h.note}</span><span className="text-slate-500">{h.by?.name}</span></li>))}</ul>
    </Panel>
  );
}

export default function AssetUnitDetail() {
  const { id } = useParams();
  const { can } = useMe();
  const { data: u, reload, error } = useFetch(`/assets/units/${id}`);
  const { data: group } = useFetch(u ? `/assets/groups/${u.group_id}` : null);
  const { data: meta } = useFetch("/assets/meta");
  const [edit, setEdit] = useState(false);
  const [tick, setTick] = useState(0);
  if (error) return <div className="text-red-600 text-sm">{error}</div>;
  if (!u) return <div className="text-sm text-slate-500">Loading…</div>;
  const refresh = () => { reload(); setTick((t) => t + 1); };
  const toggle = async () => {
    const reason = window.prompt(u.active ? "Reason for deactivating this unit (e.g. replaced, scrapped)?" : "Reason for reactivating?");
    if (!reason) return;
    try { await api.post(`/assets/units/${u.id}/status`, { reason, active: !u.active }); refresh(); } catch (e) { toast.error(errMsg(e)); }
  };
  const r = u.group_refs || {};
  return (
    <div data-testid="asset-unit-detail-page">
      <Link to="/assets/units" className="text-xs text-slate-500 hover:underline">← Asset Units</Link>
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3 mt-1 mb-4">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="text-2xl font-bold tracking-tight"><span className="font-mono">{u.asset_code}</span> · {u.unit_name}</h1>
            <StatusBadge status={u.lifecycle_status} testid="au-lifecycle-badge" /><StatusBadge status={u.unit_status} testid="au-status-badge" /><StatusBadge status={u.warranty_state} testid="au-warranty-badge" />
            {!u.active && <StatusBadge status="inactive" testid="au-inactive-badge" />}
          </div>
          <AuditMeta record={u} />
        </div>
        {can("write") && <div className="flex gap-2">
          {u.active && group?.status === "active" && <Button size="sm" variant="outline" onClick={() => setEdit(true)} data-testid="au-edit-btn"><Pencil className="h-4 w-4 mr-1" />Edit</Button>}
          <Button size="sm" variant="outline" onClick={toggle} data-testid="au-toggle-active-btn">{u.active ? "Deactivate" : "Reactivate"}</Button>
          <LifecycleAction u={u} onDone={refresh} />
        </div>}
      </div>
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4 mb-4">
        <Panel title="Identity" testid="au-identity-panel">
          <div className="grid grid-cols-2 gap-3">
            <Field k="Asset type" v={u.asset_type} /><Field k="Quantity" v={String(u.quantity)} mono />
            <Field k="Added as" v={<span data-testid="au-addition-mode" className={u.addition_mode === "historical" || u.addition_mode === "direct" ? "text-amber-800" : ""}>{u.addition_label}{u.source_dc_no ? ` · DC ${u.source_dc_no}` : ""}</span>} /><div />
            <Field k="Serial no." v={u.serial_no} mono /><Field k="Mfr serial no." v={u.mfr_serial_no} mono />
            <Field k="Model" v={u.model_no} /><Field k="Item" v={u.item_snapshot ? `${u.item_snapshot.item_code} – ${u.item_snapshot.item_name}` : ""} />
            <div className="col-span-2"><Field k="Capacity / spec" v={u.capacity_spec} /></div>
          </div>
        </Panel>
        <Panel title="Installed base link" testid="au-link-panel">
          <div className="grid grid-cols-2 gap-3">
            <Field k="Asset group" v={<Link className="text-sky-700 font-mono" to={`/assets/groups/${u.group_id}`}>{u.group_code}</Link>} />
            <Field k="Customer" v={u.customer_snapshot?.name} />
            <Field k="Project ref" v={r.project_ref} mono /><Field k="Invoice ref(s)" v={r.invoice_refs} mono />
            <Field k="Sales order / LM" v={[r.sales_order_ref, r.lm_ref].filter(Boolean).join(" / ")} mono /><Field k="Service owner" v={u.service_owner} />
          </div>
        </Panel>
        <Panel title="Dates, warranty & AMC" testid="au-dates-panel">
          <div className="grid grid-cols-2 gap-3">
            {DATES.map(([k, l]) => <Field key={k} k={l} v={fmtDate(u[k])} />)}
            <Field k="Warranty" v={`${fmtDate(u.warranty_start)} → ${fmtDate(u.warranty_end)}`} />
            <Field k="AMC" v={u.amc_end ? `${fmtDate(u.amc_start)} → ${fmtDate(u.amc_end)}` : "No AMC"} />
            <div className="col-span-2"><Field k="Notes" v={u.notes} /></div>
          </div>
        </Panel>
      </div>
      <div className="mb-4"><LifecycleHistory u={u} /></div>
      <Tabs defaultValue="history" className="bg-white border border-slate-200 rounded-md">
        <TabsList className="w-full justify-start rounded-none border-b border-slate-200 bg-slate-50 h-auto p-1">
          <TabsTrigger value="history" data-testid="au-tab-history">History</TabsTrigger>
          <TabsTrigger value="docs" data-testid="au-tab-documents">Documents</TabsTrigger>
        </TabsList>
        <div className="p-4">
          <TabsContent value="history"><AuditPanel entityId={u.id} refreshKey={tick} /></TabsContent>
          <TabsContent value="docs"><DocumentsPanel entityType="asset_unit" entityId={u.id} onChange={() => setTick((t) => t + 1)} /></TabsContent>
        </div>
      </Tabs>
      {group && <AssetUnitForm open={edit} onOpenChange={setEdit} group={group} unit={u} types={meta?.asset_types} onSaved={refresh} />}
    </div>
  );
}
